import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Dumbbell, Zap, Crown, TrendingUp, ArrowUpRight } from "lucide-react";
import { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import OnboardingPopup from "@/components/OnboardingPopup";
import { JourneyPill } from "@/components/JourneyPill";
import { GoalsWidget } from "@/components/athlete/GoalsWidget";
import { FoodTrackerWidget } from "@/components/athlete/FoodTrackerWidget";
import { StaySurveyModal } from "@/components/StaySurveyModal";

/**
 * AthleteDashboardContent — the full inner dashboard an athlete sees on
 * sign-in. Extracted so the admin "Athlete view" tab can render the
 * EXACT same component (no drift between admin preview and the real flow).
 *
 * Renders without a top navbar; the parent (DashboardPage or AdminDashboard)
 * is expected to provide chrome.
 *
 * Props:
 *  - showOnboardingFlow (default true) — whether to fire the OnboardingPopup
 *    questionnaire. Admin preview passes `false` so the modal doesn't pop.
 */
export const AthleteDashboardContent = ({ showOnboardingFlow = true }) => {
  const { user } = useAuth();
  const [stats, setStats] = useState({ workouts: 0, streak: 0, progress: [] });
  const [showOnboarding, setShowOnboarding] = useState(showOnboardingFlow);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await fetch(`${API}/workouts/progress/history?limit=7`, { credentials: "include" });
        if (response.ok) {
          const data = await response.json();
          setStats({ workouts: data.length, streak: data.length, progress: data });
        }
      } catch (error) {
        console.error("Failed to fetch stats");
      }
    };
    fetchStats();
  }, []);

  return (
    <>
      {showOnboarding && user?.role === "user" && (
        <OnboardingPopup onComplete={() => setShowOnboarding(false)} />
      )}
      {showOnboardingFlow && <StaySurveyModal />}
      <div className="max-w-7xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-12">
          <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight mb-2">
            WELCOME BACK, <span className="gradient-text">{user?.name?.toUpperCase()}</span>
          </h1>
          <p className="text-text-secondary">
            Current plan: <span className="text-primary font-semibold capitalize">{user?.subscription_tier}</span>
            {(user?.subscription_tier === "free" || !user?.subscription_tier) && (
              <button onClick={() => navigate("/")} className="ml-4 text-accent hover:underline">Get Started</button>
            )}
          </p>
        </motion.div>

        {/* My Paladin Journey — compact entry point to /journey */}
        <div className="mb-8">
          <JourneyPill />
        </div>

        {/* My Goals — up to 5 personal goals with progress */}
        <GoalsWidget />

        {/* Today's Food — daily macro tracker */}
        <FoodTrackerWidget />

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-12">
          {[
            { label: "Workouts This Week", value: stats.workouts, icon: Dumbbell },
            { label: "Current Streak", value: `${stats.streak} days`, icon: Zap },
            { label: "Subscription", value: user?.subscription_tier || "Free", icon: Crown },
            { label: "Progress", value: "On Track", icon: TrendingUp },
          ].map((stat, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}
              className="bg-surface border border-border rounded-xl p-6" data-testid={`stat-${stat.label.toLowerCase().replace(/\s/g, '-')}`}>
              <stat.icon className="w-8 h-8 text-primary mb-3" />
              <p className="text-text-muted text-sm">{stat.label}</p>
              <p className="font-heading text-2xl font-bold capitalize">{stat.value}</p>
            </motion.div>
          ))}
        </div>

        {/* Upgrade Banner — show for foundations users */}
        {user?.subscription_tier === "foundations" && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-12 bg-gradient-to-r from-emerald-500/10 to-amber-500/10 border border-emerald-500/30 rounded-2xl p-6"
            data-testid="upgrade-banner"
          >
            <p className="text-sm font-bold text-emerald-400 uppercase tracking-wider mb-2">Ready to Level Up?</p>
            <p className="text-white/70 text-sm mb-4">Upgrade to an Accelerator for elite programming, in-depth nutrition, and faster results.</p>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => navigate("/accelerator/home-gym")}
                className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold px-5 py-2.5 rounded-xl transition-colors text-sm"
                data-testid="upgrade-homegym-btn"
              >
                Home Gym — $40/wk <ArrowUpRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => navigate("/accelerator/sports-performance")}
                className="flex items-center gap-2 bg-rose-600 hover:bg-rose-500 text-white font-semibold px-5 py-2.5 rounded-xl transition-colors text-sm"
                data-testid="upgrade-sports-btn"
              >
                Sports Performance — $40/wk <ArrowUpRight className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}

        <h2 className="font-heading text-2xl font-bold mb-6">RECENT ACTIVITY</h2>
        <div className="bg-surface border border-border rounded-xl p-6">
          {stats.progress.length > 0 ? (
            <div className="space-y-4">
              {stats.progress.slice(0, 5).map((entry, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center">
                      <Dumbbell className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-semibold">{entry.workout_id}</p>
                      <p className="text-sm text-text-muted">{entry.duration_minutes} min</p>
                    </div>
                  </div>
                  <p className="text-text-secondary text-sm">{new Date(entry.completed_at).toLocaleDateString()}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Dumbbell className="w-12 h-12 text-text-muted mx-auto mb-4" />
              <p className="text-text-secondary">No workouts completed yet</p>
              <button onClick={() => navigate("/workouts")} className="text-primary hover:underline mt-2">Start your first workout</button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AthleteDashboardContent;
